// src/app/components/admin/provider-throttle-setting.tsx
//
// Platform section of Global Settings: how fast measurement runs are allowed
// to call a provider.
//
// The setting that matters is the Perplexity start rate. Perplexity's Agent
// API — which every Perplexity lane calls now that Sonar is retired — limits
// QUERIES PER SECOND, per API organisation, so it is one budget shared by every
// run happening at once. Set it above the account's real tier and runs start
// losing answers to "429 - Request rate limit exceeded"; one 250-call run lost
// 106 that way. A lost call is not a neutral gap either — it is scored as an
// explicit zero, so it moves the brand's mention rate.
//
// The tier is the account's, not the code's: it changes when someone upgrades
// it in the Perplexity console, and it has to come down the minute a run starts
// failing. That is why this is here and not in an env file. Values are stored
// in app_config; the measurement workers re-read within about 30 seconds, so a
// run already in flight slows down without a deploy or a restart.

import { useEffect, useState } from "react";
import { Check, Gauge, Loader2 } from "lucide-react";

import {
  providerThrottleApi,
  type ProviderThrottleSettings,
  type ProviderThrottleUpdate,
} from "@/api/provider-throttle";

/** Field order on the page: the one that causes the outage first. */
const FIELDS = [
  {
    key: "perplexity_agent_qps" as const,
    label: "Perplexity start rate",
    unit: "queries per second",
    help:
      "Your Perplexity account's Agent API tier. Shared across every run happening at once. Above your real tier, runs lose calls to 429 errors. 0 turns pacing off.",
    step: 1,
    min: (s: ProviderThrottleSettings) => s.limits.min_qps,
    max: (s: ProviderThrottleSettings) => s.limits.max_qps,
    parse: (raw: string) => Number.parseFloat(raw),
  },
  {
    key: "max_concurrent_calls" as const,
    label: "Calls at once",
    unit: "per provider",
    help:
      "How many calls to one provider run in parallel. Bounds total pressure; it does not bound the start rate. Takes effect on the next run. 0 is uncapped.",
    step: 1,
    min: (s: ProviderThrottleSettings) => s.limits.min_concurrent_calls,
    max: (s: ProviderThrottleSettings) => s.limits.max_concurrent_calls,
    parse: (raw: string) => Number.parseInt(raw, 10),
  },
  {
    key: "rate_limit_max_retries" as const,
    label: "Retries after a 429",
    unit: "extra attempts",
    help:
      "How many times a rate-limited call is retried, waiting as long as the provider asks, before it is recorded as a failure and scored as a zero.",
    step: 1,
    min: (s: ProviderThrottleSettings) => s.limits.min_retries,
    max: (s: ProviderThrottleSettings) => s.limits.max_retries,
    parse: (raw: string) => Number.parseInt(raw, 10),
  },
];

type FieldKey = (typeof FIELDS)[number]["key"];

export function ProviderThrottleSetting() {
  const [settings, setSettings] = useState<ProviderThrottleSettings | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [savedAt, setSavedAt] = useState(0);

  function adopt(next: ProviderThrottleSettings) {
    setSettings(next);
    setDrafts({
      perplexity_agent_qps: String(next.perplexity_agent_qps),
      max_concurrent_calls: String(next.max_concurrent_calls),
      rate_limit_max_retries: String(next.rate_limit_max_retries),
    });
  }

  useEffect(() => {
    let alive = true;
    providerThrottleApi
      .get()
      .then((next) => alive && adopt(next))
      .catch((e) => alive && setErr(String(e?.message || e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  async function save(changes: ProviderThrottleUpdate) {
    if (saving || !settings) return;
    setSaving(true);
    setErr("");
    try {
      adopt(await providerThrottleApi.update(changes));
      setSavedAt(Date.now());
    } catch (e: any) {
      setErr(String(e?.message || e));
      // Put the inputs back to what is actually stored, so a rejected value
      // never sits in the box looking like it took.
      adopt(settings);
    } finally {
      setSaving(false);
    }
  }

  const disabled = loading || saving;

  function parsed(key: FieldKey) {
    const field = FIELDS.find((f) => f.key === key)!;
    const value = field.parse(drafts[key] ?? "");
    if (!settings || !Number.isFinite(value)) return null;
    if (value < field.min(settings) || value > field.max(settings)) return null;
    return value;
  }

  function dirty(key: FieldKey) {
    const value = parsed(key);
    return value !== null && !!settings && value !== settings[key];
  }

  const changed = FIELDS.filter((f) => dirty(f.key));

  return (
    <div className="py-6">
      <div className="max-w-2xl">
        <h2
          style={{
            fontSize: "var(--text-lg)",
            fontWeight: "var(--font-weight-semibold)",
            color: "var(--foreground)",
          }}
        >
          Provider call limits
        </h2>
        <p
          style={{
            fontSize: "var(--text-sm)",
            color: "var(--muted-foreground)",
            marginTop: 4,
          }}
        >
          How fast measurement runs are allowed to call each AI provider. These exist because
          Perplexity's Agent API limits queries per second across your whole account — over that
          limit, runs lose answers to rate-limit errors, and a lost answer is scored as a zero.
          Changes reach running batteries within about 30 seconds.
        </p>

        {err && (
          <div
            className="mt-4 rounded-lg px-3 py-2"
            style={{
              fontSize: "var(--text-sm)",
              color: "var(--error-text)",
              backgroundColor: "var(--error-bg)",
            }}
          >
            {err}
          </div>
        )}

        {settings && (
          <div className="mt-5">
            <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
              Your Perplexity tier — pick the one your account has in the Perplexity console.
              Higher than your real tier is what causes the errors.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {Object.entries(settings.perplexity_tier_qps).map(([tier, qps]) => {
                const isCurrent = settings.perplexity_agent_qps === qps;
                return (
                  <button
                    key={tier}
                    type="button"
                    disabled={disabled}
                    onClick={() => save({ perplexity_agent_qps: qps })}
                    className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 transition-all"
                    style={{
                      cursor: disabled ? "default" : "pointer",
                      fontSize: "var(--text-sm)",
                      fontWeight: "var(--font-weight-medium)",
                      borderColor: isCurrent ? "var(--alpha-teal)" : "var(--border)",
                      backgroundColor: isCurrent
                        ? "var(--alpha-teal-10)"
                        : "var(--card, transparent)",
                      color: isCurrent ? "var(--alpha-teal)" : "var(--foreground)",
                      boxShadow: isCurrent ? "0 0 0 1px var(--alpha-teal)" : "none",
                      opacity: loading ? 0.6 : 1,
                    }}
                  >
                    {isCurrent && <Check className="h-3.5 w-3.5" />}
                    Tier {tier} · {qps}/s
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-col gap-5">
          {FIELDS.map((field) => {
            const draft = drafts[field.key] ?? "";
            const value = parsed(field.key);
            const min = settings ? field.min(settings) : 0;
            const max = settings ? field.max(settings) : 0;
            return (
              <div key={field.key}>
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg"
                    style={{
                      backgroundColor: "var(--muted, #f1f5f9)",
                      color: "var(--muted-foreground)",
                    }}
                  >
                    <Gauge className="h-4.5 w-4.5" />
                  </span>

                  <label
                    htmlFor={`throttle-${field.key}`}
                    style={{
                      fontSize: "var(--text-sm)",
                      fontWeight: "var(--font-weight-medium)",
                      color: "var(--foreground)",
                      minWidth: 170,
                    }}
                  >
                    {field.label}
                  </label>

                  <input
                    id={`throttle-${field.key}`}
                    type="number"
                    step={field.step}
                    min={min}
                    max={max}
                    value={draft}
                    disabled={disabled}
                    onChange={(e) =>
                      setDrafts((prev) => ({ ...prev, [field.key]: e.target.value }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && dirty(field.key) && value !== null) {
                        save({ [field.key]: value });
                      }
                    }}
                    className="w-24 rounded-lg border px-3 py-2"
                    style={{
                      fontSize: "var(--text-sm)",
                      color: "var(--foreground)",
                      backgroundColor: "var(--card, transparent)",
                      borderColor:
                        draft !== "" && value === null
                          ? "var(--error-border)"
                          : "var(--border)",
                    }}
                  />
                  <span style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
                    {field.unit} ({min}–{max})
                  </span>
                </div>
                <p
                  className="mt-1.5"
                  style={{
                    fontSize: "var(--text-xs)",
                    color: "var(--muted-foreground)",
                    lineHeight: 1.5,
                    paddingLeft: 44,
                  }}
                >
                  {field.help}
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-5">
          <button
            type="button"
            disabled={disabled || changed.length === 0}
            onClick={() => {
              const changes: ProviderThrottleUpdate = {};
              for (const field of changed) {
                const value = parsed(field.key);
                if (value !== null) changes[field.key] = value;
              }
              save(changes);
            }}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 transition-all"
            style={{
              cursor: disabled || changed.length === 0 ? "default" : "pointer",
              fontSize: "var(--text-sm)",
              fontWeight: "var(--font-weight-medium)",
              color: changed.length > 0 ? "#fff" : "var(--muted-foreground)",
              backgroundColor:
                changed.length > 0 ? "var(--alpha-teal)" : "var(--muted, #f1f5f9)",
            }}
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save
          </button>
        </div>

        {savedAt > 0 && !saving && !err && settings && (
          <p className="mt-3" style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
            Saved. Perplexity Agent calls start at most {settings.perplexity_agent_qps} per second
            across the whole account. Running batteries pick this up within about 30 seconds; the
            calls-at-once limit applies from the next run.
          </p>
        )}
      </div>
    </div>
  );
}

export default ProviderThrottleSetting;
